import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddComponent } from "@/components/catalogue/BundleComponentsCard";

export function NewBundleDialog({ open, onClose }) {
  const nav = useNavigate();
  const [f, setF] = useState({ title: "", sku: "", price: "", description: "" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [img, setImg] = useState(null);
  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) { return; }
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size > 5 * 1024 * 1024) { toast.error("Picture must be a JPG, PNG or WEBP under 5 MB"); return; }
    const rd = new FileReader();
    rd.onload = () => setImg(rd.result);
    rd.readAsDataURL(file);
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!window.confirm(`Create "${f.title}" and PUBLISH it on the /shop/ website straight away?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/new-catalogue/bundle-web-push/bundles", {
        confirm: true, title: f.title, sku: f.sku || null, selling_price: Number(f.price) || 0, description: f.description || null, image_data: img,
        components: rows.map((x) => ({ product_legacy_id: x.product_legacy_id, qty: Number(x.qty) || 0 })),
      }, { timeout: 120000 });
      toast.success(`Bundle created and published on the website (#${r.data.website_id})`);
      onClose(); nav(`/products/${r.data.legacy_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the bundle"); } finally { setBusy(false); }
  };
  const inp = "h-9 w-full rounded-md border border-slate-200 px-2 text-sm";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="new-bundle-dialog">
        <DialogHeader><DialogTitle>New bundle</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <input className={inp} placeholder="Bundle name" value={f.title} onChange={set("title")} data-testid="new-bundle-title" />
          <div className="flex gap-2">
            <input className={inp} placeholder="SKU (optional)" value={f.sku} onChange={set("sku")} data-testid="new-bundle-sku" />
            <input className={inp} type="number" min="0" step="0.01" placeholder="Price £" value={f.price} onChange={set("price")} data-testid="new-bundle-price" />
          </div>
          <textarea className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm" rows={2} placeholder="Description (optional)" value={f.description} onChange={set("description")} data-testid="new-bundle-description" />
          <div className="flex items-center gap-3">
            {img && <img src={img} alt="Bundle" className="h-16 w-16 rounded-md border border-slate-200 object-cover" data-testid="new-bundle-image-preview" />}
            <label className="cursor-pointer rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
              {img ? "Change picture" : "Add picture (optional)"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="new-bundle-image-input" />
            </label>
            {img && <button type="button" className="text-xs text-rose-600" onClick={() => setImg(null)} data-testid="new-bundle-image-remove">Remove</button>}
          </div>
          <table className="w-full text-sm">
            <tbody>{rows.map((r, i) => (
              <tr key={r.product_legacy_id} className="border-t border-slate-100">
                <td className="py-1.5">#{r.product_legacy_id} {r.title}</td>
                <td><input type="number" min="1" className="h-8 w-20 rounded border border-slate-200 px-2" value={r.qty} onChange={(e) => setRows((x) => x.map((y, j) => (j === i ? { ...y, qty: e.target.value } : y)))} data-testid={`new-bundle-qty-${r.product_legacy_id}`} /></td>
                <td><button type="button" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} data-testid={`new-bundle-remove-${r.product_legacy_id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button></td>
              </tr>))}</tbody>
          </table>
          <AddComponent taken={rows.map((r) => r.product_legacy_id)} onAdd={(p) => setRows((x) => [...x, { product_legacy_id: p.legacy_id, title: p.title, qty: 1 }])} />
          <p className="text-xs text-slate-500">Saving publishes the bundle on the /shop/ website immediately{img ? " with this picture" : ""}. Stock is taken from these Accessories products when it sells.</p>
          <Button disabled={busy || !f.title.trim() || !(Number(f.price) > 0) || !rows.length} onClick={save} data-testid="new-bundle-save">{busy ? "Publishing…" : "Create and publish"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
